<template>
  <div class="sf-colors-frame-wrap" :style="height ? { maxHeight: height } : null">
    <iframe
      ref="frame"
      class="sf-colors-frame"
      :style="height ? { height } : null"
      :src="iframeSrc"
      sandbox="allow-scripts allow-same-origin"
      referrerpolicy="strict-origin"
      title="Storefront colors preview"
      @load="onFrameLoad"
      @error="onFrameError"
    />
    <!-- Until the first ack the iframe shows the shop's saved styles, not the editor state. -->
    <div v-if="!acked" class="sf-colors-frame-loading">{{ _('Loading...') }}</div>
  </div>
</template>

<script>
// The storefront page must load within LOAD_TIMEOUT_MS, then say ready within READY_TIMEOUT_MS
// after its load event (hydration can take several seconds, e.g. on a dev server).
// Both are paused while the POS tab is hidden.
const LOAD_TIMEOUT_MS = 30000
const READY_TIMEOUT_MS = 10000
const DEBOUNCE_MS = 150
const PING_INTERVAL_MS = 500
const ACK_TIMEOUT_MS = 1500
const MAX_RESENDS = 3
// A page that never says ready (e.g. a script failed to load) is reloaded once before the fallback.
const MAX_RELOADS = 1

// The shop's own domain answers after a redirect, so the origin is not known in advance.
// The frame is identified by event.source; this only rejects non-https (and 'null') origins.
function isAcceptedOrigin (origin) {
  try {
    const url = new URL(origin)
    if (url.protocol === 'https:') {
      return true
    }
    return url.protocol === 'http:' && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')
  } catch (e) {
    return false
  }
}

export default {
  name: 'StorefrontColorsFrame',
  props: {
    origin: {
      type: String,
      required: true
    },
    locale: {
      type: String,
      required: true
    },
    css: {
      type: String,
      default: ''
    },
    // Any CSS height, e.g. 'max(600px, calc(100vh - 200px))'. Empty keeps the default 900px.
    height: {
      type: String,
      default: ''
    }
  },
  emits: ['unavailable'],
  data () {
    return {
      ready: false,
      acked: false,
      targetOrigin: null,
      seq: 0,
      pendingSeq: null,
      resends: 0,
      reloads: 0,
      readyTimer: null,
      readyRemaining: null,
      readyDeadline: null,
      pingTimer: null,
      ackTimer: null,
      debounceTimer: null,
      unavailableEmitted: false
    }
  },
  computed: {
    iframeSrc () {
      const origin = (this.origin || '').replace(/\/$/, '')
      const locale = this.locale || 'fi_FI'
      return origin + '/' + locale + '/preview/colors?embed=1'
    }
  },
  watch: {
    css () {
      if (!this.ready) {
        return
      }
      this.scheduleSendTokens()
    },
    origin () {
      this.reloads = 0
      this.resetReadyState()
    },
    locale () {
      this.reloads = 0
      this.resetReadyState()
    }
  },
  mounted () {
    window.addEventListener('message', this.onMessage)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.startReadyTimer(LOAD_TIMEOUT_MS)
  },
  beforeUnmount () {
    this.cleanup()
  },
  methods: {
    cleanup () {
      window.removeEventListener('message', this.onMessage)
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      this.clearTimers()
    },
    clearTimers () {
      this.stopReadyTimer()
      this.stopPings()
      this.stopAckTimer()
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = null
      }
    },
    startReadyTimer (ms) {
      this.stopReadyTimer()
      this.readyRemaining = ms
      if (!document.hidden) {
        this.resumeReadyTimer()
      }
    },
    resumeReadyTimer () {
      if (this.readyTimer || this.readyRemaining === null) {
        return
      }
      this.readyDeadline = Date.now() + this.readyRemaining
      this.readyTimer = setTimeout(() => {
        this.readyTimer = null
        this.readyRemaining = null
        this.onReadyTimeout()
      }, this.readyRemaining)
    },
    pauseReadyTimer () {
      if (!this.readyTimer) {
        return
      }
      clearTimeout(this.readyTimer)
      this.readyTimer = null
      this.readyRemaining = Math.max(0, this.readyDeadline - Date.now())
    },
    stopReadyTimer () {
      if (this.readyTimer) {
        clearTimeout(this.readyTimer)
        this.readyTimer = null
      }
      this.readyRemaining = null
    },
    onVisibilityChange () {
      if (document.hidden) {
        this.pauseReadyTimer()
      } else {
        this.resumeReadyTimer()
      }
    },
    // A new src is a new document: wait for its ready before sending again.
    resetReadyState () {
      this.clearTimers()
      this.ready = false
      this.acked = false
      this.targetOrigin = null
      this.pendingSeq = null
      this.resends = 0
      this.unavailableEmitted = false
      this.startReadyTimer(LOAD_TIMEOUT_MS)
    },
    onReadyTimeout () {
      const frame = this.$refs.frame
      if (!this.ready && !this.unavailableEmitted && frame && this.reloads < MAX_RELOADS) {
        this.reloads++
        this.resetReadyState()
        frame.src = this.iframeSrc
        return
      }
      this.emitUnavailable()
    },
    onFrameLoad () {
      if (!this.ready && !this.unavailableEmitted) {
        this.startReadyTimer(READY_TIMEOUT_MS)
        this.startPings()
      }
    },
    onFrameError () {
      this.emitUnavailable()
    },
    emitUnavailable () {
      if (this.unavailableEmitted) {
        return
      }
      this.unavailableEmitted = true
      this.clearTimers()
      this.$emit('unavailable')
    },
    // Payload-free, so it may go to '*': the final origin is unknown after a redirect.
    // A storefront that does not know the ping ignores it.
    startPings () {
      this.stopPings()
      this.pingTimer = setInterval(() => {
        const frame = this.$refs.frame
        if (this.ready || !frame || !frame.contentWindow) {
          this.stopPings()
          return
        }
        try {
          frame.contentWindow.postMessage({ type: 'johku-colors-preview-ping' }, '*')
        } catch (e) {
          // ignore
        }
      }, PING_INTERVAL_MS)
    },
    stopPings () {
      if (this.pingTimer) {
        clearInterval(this.pingTimer)
        this.pingTimer = null
      }
    },
    stopAckTimer () {
      if (this.ackTimer) {
        clearTimeout(this.ackTimer)
        this.ackTimer = null
      }
    },
    onMessage (event) {
      const frame = this.$refs.frame
      if (!frame || !frame.contentWindow || event.source !== frame.contentWindow) {
        return
      }
      if (!isAcceptedOrigin(event.origin)) {
        return
      }
      const data = event.data
      if (!data || typeof data !== 'object') {
        return
      }
      if (data.type === 'johku-colors-preview-ready') {
        if (this.unavailableEmitted) {
          return
        }
        // Every ready (also after a reload) gets the full current CSS.
        this.ready = true
        this.targetOrigin = event.origin
        this.stopReadyTimer()
        this.stopPings()
        this.sendTokens()
      } else if (data.type === 'johku-colors-preview-applied') {
        if (!this.ready || this.pendingSeq === null) {
          return
        }
        // The deployed storefront acks without seq: that acks the latest send.
        if (typeof data.seq === 'number' && data.seq !== this.pendingSeq) {
          return
        }
        this.stopAckTimer()
        this.pendingSeq = null
        this.acked = true
      }
    },
    scheduleSendTokens () {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null
        this.sendTokens()
      }, DEBOUNCE_MS)
    },
    sendTokens () {
      if (!this.ready || !this.targetOrigin) {
        return
      }
      this.seq++
      this.pendingSeq = this.seq
      this.resends = 0
      this.postTokens()
    },
    postTokens () {
      const frame = this.$refs.frame
      if (!frame || !frame.contentWindow) {
        return
      }
      try {
        frame.contentWindow.postMessage(
          { type: 'johku-colors-preview-tokens', css: this.css || '', seq: this.pendingSeq },
          this.targetOrigin
        )
      } catch (e) {
        this.emitUnavailable()
        return
      }
      this.stopAckTimer()
      this.ackTimer = setTimeout(() => {
        this.ackTimer = null
        if (this.pendingSeq === null) {
          return
        }
        if (this.resends < MAX_RESENDS) {
          this.resends++
          this.postTokens()
        } else if (!this.acked) {
          this.emitUnavailable()
        }
      }, ACK_TIMEOUT_MS)
    }
  }
}
</script>

<style scoped>
.sf-colors-frame-wrap {
  position: relative;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: auto;
  margin-bottom: 15px;
  max-height: 900px;
  background: #f7f7f7;
}
.sf-colors-frame {
  display: block;
  width: 100%;
  height: 900px;
  border: 0;
  background: #fff;
}
.sf-colors-frame-loading {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(247, 247, 247, 0.95);
  color: #777;
}
</style>
